import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Search, Package, MapPin, Layers, X, Loader2, ScanLine, Warehouse,
  Snowflake, CalendarClock, PauseCircle, ChevronDown, ChevronUp, RefreshCw,
  ArrowDownToLine, ArrowUpFromLine, History,
} from 'lucide-react';
import BarcodeScannerModal from './BarcodeScannerModal';
import { formatDate } from '../lib/dateFormat';

/**
 * Feb 2026 — Floor Stock Check panel.
 *
 * A one-touch search UI for the warehouse floor: scan or type a
 * Pallet ID / SKU / Location ID → get a stack of mobile cards
 * showing Product · On-hand · Available · Allocated · Location ·
 * Batch. Read-only — no inventory mutations.
 *
 * Props:
 *   - API: base URL (e.g. from AdminAuthContext)
 *   - authHeaders: axios auth headers
 *   - warehouseId (optional): currently-selected warehouse filter;
 *     shown as a chip so the operator knows the search scope.
 */

const DEBOUNCE_MS = 250;

const StatChip = ({ label, value, tone = 'slate', testid }) => {
  const cls = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    rose: 'bg-rose-100 text-rose-700',
    sky: 'bg-sky-100 text-sky-800',
  }[tone] || 'bg-slate-100 text-slate-700';
  return (
    <div className={`flex-1 min-w-[86px] rounded-lg px-2.5 py-1.5 ${cls}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 leading-tight">{label}</div>
      <div className="text-lg font-extrabold tabular-nums leading-tight">{value}</div>
    </div>
  );
};

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / 86400000);
};

const fmtTsShort = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}`;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hh}:${mm}`;
};

/**
 * Feb 2026 — Pallet Ledger (Floor Stock Check drilldown).
 *
 * Lazy-fetches `GET /admin/floor/pallets/{pallet_id}/ledger` when the
 * operator taps the "Pallet Ledger" toggle on a stock-check card that
 * carries a `pallet_id`. The table is designed for a handheld's ~360
 * to 420px viewport: 5 columns collapse into a 2-line grid per entry
 * (row-1: item + direction chip + qty; row-2: order + on-hand + time).
 */
const PalletLedger = ({ API, authHeaders, palletId }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [entries, setEntries] = useState([]);
  const [currentOnHand, setCurrentOnHand] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!palletId) return;
    setBusy(true); setErr('');
    try {
      const { data } = await axios.get(
        `${API}/admin/floor/pallets/${encodeURIComponent(palletId)}/ledger`,
        { headers: authHeaders, params: { limit: 30 } },
      );
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setCurrentOnHand(typeof data?.current_on_hand === 'number' ? data.current_on_hand : null);
      setLoaded(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load pallet ledger');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (palletId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [palletId]);

  return (
    <div className="px-3.5 pb-3.5 pt-1" data-testid={`pallet-ledger-${palletId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <History className="w-3.5 h-3.5" /> Pallet Ledger
          {currentOnHand !== null && (
            <span
              className="ml-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-bold tabular-nums normal-case"
              data-testid={`pallet-ledger-on-hand-${palletId}`}
            >
              On hand {currentOnHand}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#0078D4] disabled:opacity-50"
          data-testid={`pallet-ledger-refresh-${palletId}`}
        >
          <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {busy && !loaded && (
        <div className="flex items-center justify-center py-4 text-slate-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading ledger…
        </div>
      )}
      {err && (
        <div className="rounded-lg bg-rose-50 text-rose-700 text-[11px] font-semibold px-2.5 py-2" data-testid={`pallet-ledger-err-${palletId}`}>
          {err}
        </div>
      )}
      {loaded && entries.length === 0 && !err && (
        <div className="rounded-lg bg-slate-50 text-slate-500 text-[11px] font-semibold px-2.5 py-3 text-center">
          No movements recorded for this pallet.
        </div>
      )}
      {loaded && entries.length > 0 && (
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {entries.map((e) => {
            const dirIn  = e.direction === 'IN';
            const dirOut = e.direction === 'OUT';
            const chipBg = dirIn ? 'bg-emerald-100 text-emerald-800'
                        : dirOut ? 'bg-rose-100 text-rose-700'
                        : 'bg-slate-100 text-slate-600';
            const DirIcon = dirIn ? ArrowDownToLine : dirOut ? ArrowUpFromLine : null;
            return (
              <div
                key={e.id}
                className="px-2.5 py-2 text-[12px]"
                data-testid={`pallet-ledger-row-${e.id}`}
              >
                {/* Row 1 — SKU · direction · qty */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono font-bold text-[#0F172A] truncate" data-testid={`pallet-ledger-sku-${e.id}`}>
                    {e.item_code}
                  </span>
                  <span
                    className={`inline-flex items-center gap-0.5 rounded-full ${chipBg} px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider`}
                    data-testid={`pallet-ledger-dir-${e.id}`}
                  >
                    {DirIcon && <DirIcon className="w-3 h-3" />} {e.direction || e.kind_label}
                  </span>
                  <span
                    className={`ml-auto font-extrabold tabular-nums ${dirOut ? 'text-rose-700' : dirIn ? 'text-emerald-700' : 'text-slate-800'}`}
                    data-testid={`pallet-ledger-qty-${e.id}`}
                  >
                    {dirOut ? '−' : dirIn ? '+' : ''}{e.qty}
                  </span>
                </div>
                {/* Row 2 — order · on-hand · ts */}
                <div className="mt-0.5 flex items-center gap-2 text-[11px] tabular-nums text-slate-600 min-w-0">
                  <span
                    className={`truncate ${e.order_number ? 'text-[#0078D4] font-semibold' : 'text-slate-400'}`}
                    data-testid={`pallet-ledger-order-${e.id}`}
                  >
                    {e.order_number ? `#${e.order_number}` : e.kind_label}
                  </span>
                  {e.on_hand !== null && e.on_hand !== undefined && (
                    <span
                      className="rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5 text-[10px] font-bold"
                      data-testid={`pallet-ledger-onhand-${e.id}`}
                    >
                      OH {e.on_hand}
                    </span>
                  )}
                  <span className="ml-auto text-slate-400 shrink-0">{fmtTsShort(e.ts)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ResultCard = ({ row, API, authHeaders }) => {
  const [open, setOpen] = useState(false);
  const days = daysUntil(row?.batch?.expiry_date);
  const expiryTone = days == null ? 'slate' : (days < 0 ? 'rose' : days < 30 ? 'amber' : 'emerald');
  const heldOrStage = row.hold_pallet || row.stage;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`stock-check-card-${row.placement_id}`}>
      {/* Top strip — product identity + on-hand */}
      <div className="p-3.5 flex items-start gap-3">
        <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
          {row.product?.image_url
            ? <img src={row.product.image_url} alt="" className="w-full h-full object-cover" />
            : <Package className="w-6 h-6 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {row.product?.sku || '—'}
          </div>
          <div className="text-[15px] font-bold text-[#0F172A] leading-tight line-clamp-2" data-testid="stock-check-product-name">
            {row.product?.name || 'Unknown product'}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">On hand</div>
          <div className="text-2xl font-extrabold tabular-nums text-emerald-700" data-testid="stock-check-on-hand">
            {row.on_hand}
          </div>
          <div className="text-[10px] font-semibold text-slate-500 -mt-0.5">{row.product?.uom || 'ctn'}</div>
        </div>
      </div>

      {/* Location strip */}
      <div className="px-3.5 pb-2 flex flex-wrap items-center gap-1.5 text-[12px] font-semibold">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2.5 py-1">
          <ScanLine className="w-3.5 h-3.5" /> {row.pallet_id || '—'}
        </span>
        {(row.zone || row.location_id) && (
          <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 px-2.5 py-1">
            <MapPin className="w-3.5 h-3.5" />
            {[row.zone, row.location_id].filter(Boolean).join(' · ')}
          </span>
        )}
        {row.warehouse_name && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2.5 py-1">
            <Warehouse className="w-3.5 h-3.5" /> {row.warehouse_name}
          </span>
        )}
        {row.hold_pallet && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1">
            <PauseCircle className="w-3.5 h-3.5" /> HOLD
          </span>
        )}
        {row.stage && !row.hold_pallet && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-800 px-2.5 py-1">
            <Layers className="w-3.5 h-3.5" /> STAGE
          </span>
        )}
      </div>

      {/* Qty math */}
      <div className="px-3.5 pb-3 flex gap-1.5">
        <StatChip label="Available" value={row.available} tone={row.available > 0 ? 'emerald' : 'slate'} testid="stock-check-available" />
        <StatChip label="Allocated" value={row.allocated} tone={row.allocated > 0 ? 'amber' : 'slate'} testid="stock-check-allocated" />
        {heldOrStage && <StatChip label="Hold" value={row.hold} tone="rose" testid="stock-check-hold" />}
      </div>

      {/* Batch fold-out */}
      {(row.batch?.production_date || row.batch?.expiry_date || row.batch?.batch_ref) && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full px-3.5 py-2 border-t border-slate-100 flex items-center justify-between text-[12px] font-semibold text-slate-600 hover:bg-slate-50 active:bg-slate-100"
          data-testid="stock-check-toggle-batch"
        >
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" />
            Batch
            {row.batch?.expiry_date && (
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                expiryTone === 'rose' ? 'bg-rose-100 text-rose-700'
                : expiryTone === 'amber' ? 'bg-amber-100 text-amber-800'
                : 'bg-emerald-100 text-emerald-700'
              }`}>
                {days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`}
              </span>
            )}
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      )}
      {open && (
        <div className="px-3.5 pb-3.5 pt-1 text-[12.5px] text-slate-700 grid grid-cols-2 gap-2" data-testid="stock-check-batch">
          {row.batch?.production_date && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Prod date</div>
              <div className="font-semibold">{formatDate(row.batch.production_date)}</div>
            </div>
          )}
          {row.batch?.expiry_date && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Expiry</div>
              <div className="font-semibold">{formatDate(row.batch.expiry_date)}</div>
            </div>
          )}
          {row.batch?.batch_ref && (
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Batch #</div>
              <div className="font-semibold">{row.batch.batch_ref}</div>
            </div>
          )}
        </div>
      )}
      {/* Feb 2026 — Pallet Ledger. Auto-loads whenever a card carries
          a `pallet_id` so operators who scanned a pallet barcode
          land straight on the movement history. Handheld-friendly
          2-line-per-row grid; newest movements up top. */}
      {row.pallet_id && (
        <div className="border-t border-slate-100">
          <PalletLedger API={API} authHeaders={authHeaders} palletId={row.pallet_id} />
        </div>
      )}
    </div>
  );
};

const StockCheckPanel = ({ API, authHeaders, warehouseId, setLoading }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [err, setErr] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch. Aborts any in-flight request so a fast typer
  // doesn't get stale results overwriting fresh ones.
  useEffect(() => {
    setErr('');
    const term = q.trim();
    if (!term) { setResults([]); setTruncated(false); return; }
    const handle = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/admin/floor/stock-check`, {
          headers: authHeaders,
          params: { q: term, limit: 20 },
          signal: ctrl.signal,
        });
        setResults(Array.isArray(data?.results) ? data.results : []);
        setTruncated(!!data?.truncated);
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setErr(e?.response?.data?.detail || e?.message || 'Search failed');
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q, API, authHeaders]);

  const filtered = useMemo(() => {
    if (!warehouseId) return results;
    return results.filter((r) => !r.warehouse_id || r.warehouse_id === warehouseId);
  }, [results, warehouseId]);

  const empty = !busy && !err && q.trim() && filtered.length === 0;

  return (
    <div className="px-3 sm:px-4 pb-24 pt-3" data-testid="stock-check-panel">
      {/* Sticky search bar — one-thumb reach on handhelds */}
      <div className="sticky top-0 z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 pb-3 bg-slate-50/95 backdrop-blur border-b border-slate-200">
        <div className="relative">
          <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Scan or type Pallet, SKU, or Location…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-14 pl-10 pr-24 rounded-2xl border-2 border-slate-200 bg-white text-[16px] font-semibold focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition"
            data-testid="stock-check-input"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {q && (
              <button
                type="button"
                aria-label="Clear"
                onClick={() => { setQ(''); inputRef.current?.focus(); }}
                className="w-9 h-9 rounded-xl hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center text-slate-500"
                data-testid="stock-check-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setScanOpen(true)}
              className="h-10 px-3 rounded-xl bg-slate-900 text-white text-[13px] font-bold inline-flex items-center gap-1.5 active:bg-slate-800"
              data-testid="stock-check-scan"
            >
              <ScanLine className="w-4 h-4" /> Scan
            </button>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] font-semibold text-slate-500">
          <span>
            {busy ? (
              <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</span>
            ) : q.trim() ? (
              <span data-testid="stock-check-count">{filtered.length} result{filtered.length === 1 ? '' : 's'}{truncated ? ' · showing top 20' : ''}</span>
            ) : (
              <span>Read-only lookup · No inventory changes</span>
            )}
          </span>
          {warehouseId && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2 py-0.5 text-[10px] font-bold">
              <Warehouse className="w-3 h-3" /> Scoped
            </span>
          )}
        </div>
      </div>

      {/* Results / states */}
      {!q.trim() && (
        <div className="mt-8 text-center text-slate-400" data-testid="stock-check-hint">
          <Snowflake className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <div className="text-[13px] font-semibold text-slate-500">Start by scanning a pallet or typing a SKU</div>
          <div className="text-[11px] mt-1 max-w-xs mx-auto">
            Pallet IDs · Location IDs · SKUs — matches show product, batch and live quantities.
          </div>
        </div>
      )}

      {err && (
        <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700 flex items-start gap-2" data-testid="stock-check-error">
          <div className="font-bold shrink-0">Error</div>
          <div className="flex-1">{err}</div>
          <button
            type="button"
            onClick={() => setQ((v) => v.trim() ? v + ' ' : v)}
            className="text-rose-800 hover:text-rose-900"
            title="Retry"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      {empty && (
        <div className="mt-8 text-center text-slate-500" data-testid="stock-check-empty">
          <div className="text-[13px] font-semibold">No stock found for "{q.trim()}"</div>
          <div className="text-[11px] mt-1 text-slate-400">Try a partial SKU or a location code.</div>
        </div>
      )}

      <div className="mt-3 space-y-3">
        {filtered.map((r) => <ResultCard key={r.placement_id} row={r} API={API} authHeaders={authHeaders} />)}
      </div>

      <BarcodeScannerModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onResult={(v) => { setScanOpen(false); setQ(String(v || '').trim()); }}
      />
    </div>
  );
};

export default StockCheckPanel;
